import { isInternalWebsiteCtaDestination } from "./websiteCtaDestinations";
import { toPublicTabKey } from "./publicTabKey";
import { z } from "zod";

export type SubmissionConfirmationMode = "message" | "page";
export type SubmissionConfirmation = {
  mode: SubmissionConfirmationMode;
  message: string;
  destination: string;
};

export const DEFAULT_SUBMISSION_CONFIRMATION_MESSAGE = "Thank you!  If a reply is needed, we'll be in touch soon.";

/** Only website destinations may redirect a successful submission. */
export function validSubmissionConfirmationDestination(raw: string): boolean {
  const value = raw.trim();
  if (!value) return true;
  if (isInternalWebsiteCtaDestination(value) || /^#[^\s#]+$/.test(value)) return true;
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && !url.username && !url.password;
  } catch {
    return false;
  }
}

export const submissionConfirmationUrlSchema = z.string().trim().max(2000)
  .refine(validSubmissionConfirmationDestination, "Choose a website destination or enter a valid HTTP or HTTPS page URL.");
export const submissionConfirmationModeSchema = z.enum(["message", "page"]);

export function submissionConfirmation(
  mode: unknown, message: string | undefined | null, destination: string | undefined | null,
): SubmissionConfirmation {
  const value = (destination ?? "").trim();
  return {
    mode: mode === "page" && value && validSubmissionConfirmationDestination(value) ? "page" : "message",
    message: message?.trim() || DEFAULT_SUBMISSION_CONFIRMATION_MESSAGE,
    destination: validSubmissionConfirmationDestination(value) ? value : "",
  };
}

/** Resolve ordinary website-picker tokens into real navigable public addresses. */
export function submissionConfirmationHref(destination: string, homeHref: string): string | null {
  const value = destination.trim();
  if (!value || !validSubmissionConfirmationDestination(value)) return null;
  const home = homeHref.replace(/\/+$/, "") || "/";
  const path = home === "/" ? "" : home;
  if (/^https?:\/\//i.test(value)) return value;
  if (value === "blog") return `${path}/blog`;
  const page = /^page:([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(value);
  if (page) return `${path}/${page[1]}`;
  if (value === "receptionist") return `${home}?digi=general`;
  if (value === "receptionist:pricing") return `${home}?digi=pricing`;
  if (value === "session:consultation") return `${home}?session=consultation`;
  if (value === "booking") return `${home}#class-schedule`;
  const tab = /^tab:(tab-\d+|current-offers|client-feedback|sessions)$/.exec(value);
  if (tab) return `${home}?tab=${toPublicTabKey(tab[1])}`;
  const match = /^(slide|campaign):(\d+)$/.exec(value);
  if (match) return match[1] === "slide" ? `${home}?slide=${match[2]}` : `${home}#go-campaign-${match[2]}`;
  if (value.startsWith("#")) return `${home}${value}`;
  return null;
}
